import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks';
import { SearchIcon } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Input, type InputProps } from './Input';

interface DebouncedSearchBarProps extends Omit<
  InputProps,
  'onChange' | 'value'
> {
  searchKey: string;
  debounce?: number;
  updateParams: (params: Record<string, string>) => void;
  defaultValue?: string;
}

/**
 * A search bar component that debounces the input value.
 * The component will update the `updateParams` function with the debounced value
 * and the `searchKey` as the key.
 * The component will also render a search icon as the prefix of the input field.
 *
 * @param {string} [placeholder="Search..."] - The placeholder string to display in the input field.
 * @param {string} searchKey - The key to use when updating the `updateParams` function.
 * @param {number} [debounce=400] - The debounce time in milliseconds.
 * @param {function} updateParams - The function to update the params with the debounced value.
 * @param {string} [defaultValue=""] - The default value of the input field.
 * @param {InputProps} props - Additional props to pass to the underlying Input component.
 */

export function SearchBar({
  placeholder = 'Search...',
  searchKey,
  debounce = 400,
  updateParams,
  defaultValue = '',
  className = '',
  ...props
}: DebouncedSearchBarProps) {
  const [value, setValue] = useState(defaultValue);
  const debouncedValue = useDebounce(value, debounce);
  // The last term this input and its driving state agree on. Comparing
  // against it — rather than against `defaultValue` directly — is what tells
  // an external change apart from the echo of our own commit coming back.
  const settledValue = useRef(defaultValue);

  useEffect(() => {
    if (debouncedValue === settledValue.current) return;
    settledValue.current = debouncedValue;
    updateParams({ [searchKey]: debouncedValue });
  }, [debouncedValue, searchKey, updateParams]);

  // Keep the input in sync when the driving state is cleared/changed
  // externally (e.g. removing an active-filter chip), not just when our own
  // debounced value gets committed. Echoes of our own commit are skipped, so
  // a commit landing mid-typing can never reset the field under the user.
  useEffect(() => {
    if (defaultValue === settledValue.current) return;
    settledValue.current = defaultValue;
    setValue(defaultValue);
  }, [defaultValue]);

  return (
    <Input
      {...props}
      className={cn('search-bar h-10 text-sm', className)}
      placeholder={placeholder}
      IconPrefix={SearchIcon}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}
